import { createContext, useContext, useEffect, useState } from 'react';
import { useFeature, useFeatureStrict } from '../lib/features';
import { NavLink, Outlet } from 'react-router-dom';
import { ZmistLogo } from '../components/ui/ZmistLogo';
import { LayoutGrid, Database, Compass, Layers, Cable, Settings, Scale, User, LogOut, Sparkles, Activity, Rss } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Toast } from '../components/ui/Toast';
import { UnreadBadge } from '../components/ui/UnreadBadge';
import { useStore } from '../store/StoreContext';

// Still the full tab set for the mobile bottom bar (a horizontal strip,
// not a "corner" - Settings stays a normal tab there, since mobile has
// no separate sidebar to hang a nickname/Legal group off of). The
// desktop sidebar below renders Channels/Showcase/Templates from this same
// list but replaces the Settings tab with its own bottom-left group
// (nickname -> Settings, Legal, Logout) instead of mapping this whole
// array directly. Diagnostics is appended at runtime (see useTabs) only
// when Settings > Connection has switched it on.
const TABS: { to: string; label: string; icon: LucideIcon }[] = [
  { to: '/channels', label: 'Channels', icon: LayoutGrid },
  { to: '/data', label: 'Data', icon: Database },
  { to: '/showcase', label: 'Showcase', icon: Compass },
  { to: '/templates', label: 'Schemas', icon: Layers },
  { to: '/integrations', label: 'Sources', icon: Cable },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const FOLLOWING_TAB: { to: string; label: string; icon: LucideIcon } = { to: '/following', label: 'Following', icon: Rss };

const DIAGNOSTICS_TAB: { to: string; label: string; icon: LucideIcon } = {
  to: '/diagnostics',
  label: 'Diagnostics',
  icon: Activity,
};

// Every screen renders inside a centered max-w-3xl column by default - fine
// for forms/lists, but a screen that wants to lay content out side by side
// (e.g. ChatScreen's Messages/Charts/Reports columns, which need real room
// to do that) can opt out via useWideLayout(true) instead of each such
// screen reimplementing its own break-out-of-the-column CSS.
const SetWideLayoutContext = createContext<((wide: boolean) => void) | null>(null);

// Resets back to the default (narrow) layout on unmount or when `wide`
// itself flips back to false, so navigating away from a wide screen never
// leaves an unrelated one stuck full-width.
export function useWideLayout(wide: boolean) {
  const setWide = useContext(SetWideLayoutContext);
  useEffect(() => {
    setWide?.(wide);
    return () => setWide?.(false);
  }, [wide, setWide]);
}

export function AppLayout() {
  const [wide, setWide] = useState(false);
  const templatesOn = useFeature('templates');
  const sourcesOn = useFeature('integrations.mappings');
  const followingOn = useFeatureStrict('following.v1');
  const { profile, logout, errorMessage, setErrorMessage, isSyncing, diagnosticsMenuEnabled, unreadTotal } = useStore();

  // Diagnostics sits just before Settings when enabled - Settings is always
  // the last item (and on desktop it's pulled out into its own bottom-left
  // group, so this ordering only shows through on the mobile bottom bar).
  const withFollowing = followingOn ? [TABS[0], FOLLOWING_TAB, ...TABS.slice(1)] : TABS;
  const base = diagnosticsMenuEnabled ? [...withFollowing.slice(0, -1), DIAGNOSTICS_TAB, withFollowing[withFollowing.length - 1]] : withFollowing;
  // Tabs whose feature a ZMAP admin has switched off disappear.
  const tabs = base.filter((t) => (t.to !== '/templates' || templatesOn) && (t.to !== '/integrations' || sourcesOn));

  return (
    <div className="min-h-screen w-full bg-[var(--color-bg)] flex">
      {/* First real use of isSyncing (see StoreContext.tsx's own comment) -
          a subtle, global "something's fetching" signal, same idea as
          GitHub/YouTube's top progress bar, rather than a per-screen
          spinner for every individual syncChannels/syncTemplates/
          loadMessages call. Fixed so it stays visible regardless of
          scroll position, and covers the full viewport width rather than
          just the content column so it reads as "the app", not "this
          screen". prefers-reduced-motion: the pulse is decorative, not
          load-bearing information (the bar's mere presence already
          conveys "syncing") - hidden outright rather than left animating. */}
      {isSyncing && (
        <div
          className="fixed top-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 motion-safe:animate-pulse z-50"
          role="status"
          aria-label="Syncing"
        />
      )}
      <aside className="hidden md:flex md:flex-col w-64 shrink-0 border-r border-black/10 dark:border-white/10 p-5 pb-14 gap-1 h-screen sticky top-0">
        <div className="flex items-center gap-2 px-2 mb-6">
          <ZmistLogo className="w-9 h-9 shrink-0 overflow-visible" />
          <span className="text-lg font-semibold">Zmist</span>
        </div>
        {tabs.filter(({ to }) => to !== '/settings')?.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/10'
              }`
            }
          >
            <Icon size={19} />
            {label}
            {to === '/channels' && <UnreadBadge count={unreadTotal} className="ml-auto" />}
          </NavLink>
        ))}

        {/* Bottom-left group, replacing the plain "Settings" tab: the
            account's own nickname (opening the same Profile/Appearance/
            Security hub Settings always has - see SettingsScreen.tsx)
            reads as an identity/account menu instead of a generic gear
            icon, with Legal (moved out of Settings, now its own public
            page - see LegalScreen.tsx) and Logout stacked directly below
            it. mt-auto on the first item pushes the whole group to the
            bottom without needing a separate wrapper. */}
        {/* Persistent Upgrade CTA - only while on the Free plan (see
            PlanScreen.tsx). mt-auto here pushes the whole bottom group
            down; when it's hidden the nickname link below carries mt-auto
            instead. */}
        {profile.tier === 'free' && (
          <NavLink
            to="/settings/plan"
            className="mt-auto flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-[15px] font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Sparkles size={17} className="shrink-0" />
            Upgrade plan
          </NavLink>
        )}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `${profile.tier === 'free' ? '' : 'mt-auto '}flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-medium transition-colors truncate ${
              isActive
                ? 'bg-blue-600 text-white'
                : 'text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/10'
            }`
          }
        >
          <User size={19} className="shrink-0" />
          <span className="truncate">{profile.nickname}</span>
        </NavLink>
        <NavLink
          to="/legal"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-medium transition-colors ${
              isActive
                ? 'bg-blue-600 text-white'
                : 'text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/10'
            }`
          }
        >
          <Scale size={19} />
          Legal
        </NavLink>

        <button
          type="button"
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-medium text-black/50 dark:text-white/50 hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-colors"
        >
          <LogOut size={19} />
          Logout
        </button>
      </aside>

      {/* md:pb-10 ≈ the height of the site-wide fixed footer (App.tsx /
          AppFooter) so scrolling content doesn't hide behind it; pb-20
          clears the mobile bottom tab bar. Full-height screens (ChatScreen)
          subtract the same 2.5rem in their own height calc. */}
      <main className="flex-1 min-w-0 pb-0 md:pb-[calc(2.5rem+var(--ask-dock-h,0px))]">
        <div className={`${wide ? 'max-w-none' : 'max-w-3xl'} mx-auto px-4 py-6 sm:px-6`}>
          <SetWideLayoutContext.Provider value={setWide}>
            <Outlet />
          </SetWideLayoutContext.Provider>
        </div>
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch bg-white/90 dark:bg-[#1c1c1e]/90 backdrop-blur-xl border-t border-black/10 dark:border-white/10 pb-[env(safe-area-inset-bottom)]">
        {tabs?.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `relative flex-1 flex flex-col items-center gap-0.5 pt-1.5 pb-1 text-[11px] font-medium ${
                isActive ? 'text-blue-600 dark:text-blue-400' : 'text-black/45 dark:text-white/45'
              }`
            }
          >
            <Icon size={22} strokeWidth={2.2} />
            {label}
            {to === '/channels' && unreadTotal > 0 && (
              <UnreadBadge count={unreadTotal} className="absolute top-1 left-[calc(50%+6px)]" />
            )}
          </NavLink>
        ))}
      </nav>

      {/* errorMessage/setErrorMessage already existed on the store context
          but nothing ever rendered them - every screen kept its own local
          error/Toast state instead, so a failure in code with no local
          error state of its own (patchChannel's background channel syncs,
          used by view/chart-type changes, posts, likes/follows) had
          nowhere to show up at all. This is that missing render point,
          global rather than per-screen since patchChannel can fire from
          any screen that touches a channel. */}
      {errorMessage && <Toast message={errorMessage} onDismiss={() => setErrorMessage(null)} />}
    </div>
  );
}
